import type { Business } from '@/features/booking/types/tableBookingTypes';
import type { EventBookingCategoryResponse } from '@/features/booking/types/eventBookingCategoryTypes';
import type { EventScheduleResponse } from '@/features/booking/types/eventBookingCategoryTypes';

// Cart Status 
export const CartStatus = {
  Active: 'ACTIVE',
  PendingPayment: 'PENDING_PAYMENT',
  FailedPayment: 'FAILED_PAYMENT',
  SuccessfulPayment: 'SUCCESSFUL_PAYMENT',
  Completed: 'COMPLETED',
  Refunded: 'REFUNDED',
  Abandoned: 'ABANDONED',
} as const;

export type CartStatus = typeof CartStatus[keyof typeof CartStatus];

// Cart Status display mapping
export const CartStatusLabels: Record<CartStatus, string> = {
  [CartStatus.Active]: 'Active',
  [CartStatus.PendingPayment]: 'Pending Payment',
  [CartStatus.FailedPayment]: 'Failed Payment',
  [CartStatus.SuccessfulPayment]: 'Paid',
  [CartStatus.Completed]: 'Completed',
  [CartStatus.Refunded]: 'Refunded',
  [CartStatus.Abandoned]: 'Abandoned',
};

// Item Status Code
export const ItemStatusCode = {
  // General Statuses
  NoStatus: 'NO_STATUS',
  GoodForBooking: 'GOOD_FOR_BOOKING',
  PriceUpdateRequired: 'PRICE_UPDATE_REQUIRED',
  GoodButAddOnIssues: 'GOOD_BUT_ADD_ON_ISSUES',

  // Event Booking Category Statuses
  EBCCancelled: 'EVENT_BOOKING_CATEGORY_CANCELLED',
  EBCInPast: 'EVENT_BOOKING_CATEGORY_IN_PAST',
  EBCUnavailable: 'EVENT_BOOKING_CATEGORY_UNAVAILABLE',
  EBCSoldOut: 'EVENT_BOOKING_CATEGORY_SOLD_OUT',
  EBCInsufficientCapacity: 'EVENT_BOOKING_CATEGORY_INSUFFICIENT_CAPACITY',
  TableGone: 'EVENT_BOOKING_CATEGORY_TABLE_GONE',
  TableInsufficientCapacity: 'EVENT_BOOKING_CATEGORY_TABLE_INSUFFICIENT_CAPACITY',
  CurrencyMismatch: 'EVENT_BOOKING_CATEGORY_CURRENCY_MISMATCH',

  // Add-On Statuses
  AddOnGoodForBooking: 'ADD_ON_GOOD_FOR_BOOKING',
  AddOnDisabled: 'ADD_ON_DISABLED',
  AddOnUnavailable: 'ADD_ON_UNAVAILABLE',
  AddOnInsufficientInventory: 'ADD_ON_INSUFFICIENT_INVENTORY',
} as const;

export type ItemStatusCode = typeof ItemStatusCode[keyof typeof ItemStatusCode];

// Add-On Status interface
export interface AddOnStatus {
  add_on_id: string;
  status_code: ItemStatusCode;
}

// Cart Item Status interface
export interface CartItemStatus {
  item_status_code: ItemStatusCode;
  add_on_statuses?: AddOnStatus[];
}

// SelectedAddOn represents an add-on that has been selected by a user
export interface SelectedAddOn {
  add_on_id: string;
  price: number;
  quantity: number;
}

// Add-ons per guest (array of arrays of SelectedAddOn, representing the add-ons for each guest)
export type AddOnsPerGuest = SelectedAddOn[][];

// AddOnSelection for API request bodies
export interface AddOnSelection {
  add_on_id: string;
  quantity: number;
}

// Use EventBookingCategoryResponse from eventBookingCategoryTypes


// Add-On Response interface 
export interface AddOnResponse {
  id: string;
  name: string;
  description: string;
  published: boolean;
  default_price: number | null;
  default_inventory_count: number | null;
  image_id: string | null;
  business: Business | null;
  allow_multiple_per_guest: boolean;
  is_platform_add_on: boolean;
}

// Cart Item Response interface
// Note: status is not included here as it's fetched asynchronously via getCartItemStatus
export interface CartItemResponse {
  id: string;
  total_guests: number;
  booking_time?: string | null;
  table_number?: string | null;
  table_size?: number | null;
  base_price: number;
  subtotal: number;
  platform_fee: number;
  currency: string;
  timezone_id: string;
  add_ons_per_guest: AddOnsPerGuest;
  // EventBookingCategoryResponse from cart item may have null event_schedule and booking_category
  event_booking_category: EventBookingCategoryResponse | null;
}

// Cart Response interface
export interface CartResponse {
  id: string;
  status: CartStatus;
  currency: string;
  expires_at: string;
  is_guest_cart: boolean;
  payment_intent_expires_at?: string | null;
  business: Business | null;
  items: CartItemResponse[] | null;
}

// API Response wrapper for all carts
export interface AllCartsApiResponse {
  data: CartResponse[];
}

// AddOnScheduleResponse for CartBasedEventBookingCategoryResponse
export interface AddOnScheduleResponse {
  price: number;
  inventory_count: number;
  remaining_inventory: number;
  add_on: {
    id: string;
    name: string;
    description: string;
    published: boolean;
    default_price: number | null;
    default_inventory_count: number | null;
    image_id: string | null;
    business: Business | null;
    is_platform_add_on: boolean;
  };
  event_schedule: EventScheduleResponse | null;
}

// CartBasedEventBookingCategoryResponse - DEPRECATED: No longer needed, data is now included in CartResponseWithRelatedData
export interface CartBasedEventBookingCategoryResponse {
  event_booking_category: EventBookingCategoryResponse;
  add_on_schedules: AddOnScheduleResponse[];
  cart_items: CartItemResponse[];
}

// CartItemStatusResponse - response from GET /api/booking/v1/cart/cart-item/:cartItemID/status
export interface CartItemStatusResponse {
  status: CartItemStatus;
}

// CartResponseWithRelatedData - response from GET /api/booking/v1/cart/:cartID
// This is the new response structure that includes all related data
export interface CartResponseWithRelatedData {
  cart: CartResponse;
  add_ons: AddOnResponse[];
  event_booking_categories: EventBookingCategoryResponse[];
  client_secret?: string | null;
  stripe_publishable_key?: string | null; // Connected account's publishable key (for Stripe Connect)
  stripe_account_id?: string | null; // Connected account ID
}

// CartResponseWithClientSecret - DEPRECATED: Use CartResponseWithRelatedData instead
// Keeping for backward compatibility during migration
export interface CartResponseWithClientSecret {
  cart: CartResponse;
  client_secret?: string | null;
}

// UpdateCartItemRequestBody - request body for PUT /api/booking/v1/cart/cart-item/:cartItemID
export interface UpdateCartItemRequestBody {
  total_guests: number;
  add_on_selections_per_guest: AddOnSelection[][];
}

// CheckoutResponse - response from POST /api/booking/v1/cart/:cartID/checkout
export interface CheckoutResponse {
  client_secret: string;
  stripe_publishable_key?: string; // Connected account's publishable key (for Stripe Connect)
  stripe_account_id?: string; // Connected account ID
}

// GuestCheckoutRequestBody - request body for POST /api/booking/v1/cart/:cartID/checkout-guest
export interface GuestCheckoutRequestBody {
  email: string;
  first_name: string;
  last_name: string;
}

// GuestCheckoutFormData - form data for guest checkout form
export interface GuestCheckoutFormData {
  email: string;
  firstName: string;
  lastName: string;
}

// GuestCheckoutFormErrors - validation errors for guest checkout form
export interface GuestCheckoutFormErrors {
  email?: string;
  firstName?: string;
  lastName?: string;
  general?: string;
}

// AddOnConflict - represents an inventory shortage for a specific add-on on a specific schedule
export interface AddOnConflict {
  add_on_id: string;
  event_date: string; // ISO date string
  event_name: string;
  requested_quantity: number;
  available_quantity: number;
}

// CartConflictReport - response from GET /api/booking/v1/cart/:cartID/conflict-report
export interface CartConflictReport {
  has_conflict: boolean;
  // Key = CartItemID, Value = Array of CartItemIDs that conflict with it
  availability_conflicts: Record<string, string[]>;
  add_on_conflicts: AddOnConflict[];
}

// InvoiceDownloadResponse - response from GET /api/booking/v1/cart/:cartID/invoice
export interface InvoiceDownloadResponse {
  download_url: string;
}